import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PaperPlaneTilt, ShareNetwork, ShieldCheck, Heart, EyeSlash, ArrowClockwise } from "@phosphor-icons/react";
import ShareDialog from "@/components/ShareDialog";

export default function MatchCard({ match, compact, hidden, onChanged }) {
  const nav = useNavigate();
  const [openShare, setOpenShare] = useState(false);
  const [busy, setBusy] = useState(false);
  const startChat = async () => {
    try {
      await api.post("/messages", {
        to_user_id: match.user_id,
        body: `Hi ${match.anon_name}, your journey looks so similar to mine. Would love to connect.`,
        ttl: "24h",
      });
      toast.success(`Message sent to ${match.anon_name}`);
      nav("/app/messages");
    } catch { toast.error("Could not start conversation"); }
  };
  const hide = async () => {
    setBusy(true);
    try {
      await api.post(`/matches/${match.user_id}/hide`);
      toast.success("Match hidden, they won't be notified.");
      onChanged?.();
    } catch { toast.error("Could not hide"); }
    setBusy(false);
  };
  const unhide = async () => {
    setBusy(true);
    try {
      await api.post(`/matches/${match.user_id}/unhide`);
      toast.success(`${match.anon_name} restored.`);
      onChanged?.();
    } catch { toast.error("Could not restore"); }
    setBusy(false);
  };

  return (
    <div className={`bg-white border border-border rounded-2xl p-5 shadow-soft hover:shadow-card transition-shadow ${
      compact ? "" : "h-full"
    }`} data-testid={`match-${match.user_id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-coral-50 border border-coral-100 grid place-items-center font-display text-sm text-coral-700">
            {match.anon_name.slice(0, 2)}
          </div>
          <div>
            <div className="font-display text-lg leading-tight flex items-center gap-1.5">
              {match.anon_name}
              {match.phone_verified && (
                <ShieldCheck size={14} weight="duotone" className="text-sage-700"
                  aria-label="Phone verified" data-testid={`verified-badge-${match.user_id}`} />
              )}
              {match.loss_shared && (
                <Heart size={14} weight="duotone" className="text-coral-700"
                  aria-label="Also carries a loss" data-testid={`loss-badge-${match.user_id}`} />
              )}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {match.stage} · {match.age_range}
            </div>
          </div>
        </div>
        <ScoreRing score={match.score} />
      </div>
      {!compact && match.bio && <p className="mt-4 text-sm text-ink/70 leading-relaxed italic">"{match.bio}"</p>}

      {!compact && match.peer_anniversary && (
        <div className="mt-3 flex items-start gap-2 p-3 bg-sage-50 border border-sage-100 rounded-xl" data-testid={`peer-ann-${match.user_id}`}>
          <Heart size={14} weight="duotone" className="text-coral-700 shrink-0 mt-0.5" />
          <div className="text-xs text-ink/75 leading-relaxed">
            {match.peer_anniversary.on_day
              ? <>A tender date {match.peer_anniversary.label ? `,  ${match.peer_anniversary.label} ` : ""}is <strong>today</strong>. Consider sending extra warmth.</>
              : match.peer_anniversary.days_until > 0
                ? <>A tender date is coming up for her in <strong>{match.peer_anniversary.days_until} day{match.peer_anniversary.days_until > 1 ? "s" : ""}</strong>.</>
                : <>She just passed a tender date.</>}
          </div>
        </div>
      )}

      <div className="mt-4 space-y-2">
        {["conditions","treatments","goals"].map(k => (
          match.shared?.[k]?.length > 0 && (
            <div key={k} className="flex flex-wrap gap-1.5">
              <span className="text-[10px] tracking-[0.15em] uppercase text-sage-600 mr-1 mt-1.5">{k}</span>
              {match.shared[k].slice(0, compact ? 2 : 5).map(x => (
                <span key={x} className="text-xs px-2 py-0.5 rounded-full bg-sage-50 text-sage-700 border border-sage-100">{x}</span>
              ))}
            </div>
          )
        ))}
      </div>

      {!compact && !hidden && (
        <div className="mt-5 flex items-center gap-2">
          <button onClick={startChat}
                  data-testid={`match-connect-${match.user_id}`}
                  className="flex-1 flex items-center justify-center gap-2 bg-ink text-cream py-2.5 rounded-full text-sm hover:bg-coral transition-colors">
            <PaperPlaneTilt size={14} weight="fill" /> Send first message
          </button>
          <button onClick={() => setOpenShare(true)}
                  data-testid={`match-share-${match.user_id}`}
                  aria-label="Share with a friend"
                  className="p-2.5 rounded-full border border-border hover:border-coral hover:text-coral-700 transition-colors">
            <ShareNetwork size={16} weight="duotone" />
          </button>
          <button onClick={hide} disabled={busy}
                  data-testid={`match-hide-${match.user_id}`}
                  aria-label="Silently hide this match"
                  title="Hide (they won't be notified)"
                  className="p-2.5 rounded-full border border-border hover:border-ink text-muted-foreground hover:text-ink transition-colors disabled:opacity-60">
            <EyeSlash size={16} weight="duotone" />
          </button>
        </div>
      )}
      {!compact && hidden && (
        <div className="mt-5">
          <button onClick={unhide} disabled={busy}
                  data-testid={`match-unhide-${match.user_id}`}
                  className="w-full flex items-center justify-center gap-2 border border-border py-2.5 rounded-full text-sm hover:border-coral hover:text-coral-700 transition-colors disabled:opacity-60">
            <ArrowClockwise size={14} weight="duotone" /> Restore to matches
          </button>
        </div>
      )}
      <ShareDialog open={openShare} onClose={() => setOpenShare(false)} match={match} />
    </div>
  );
}

function ScoreRing({ score }) {
  const r = 22, c = 2 * Math.PI * r;
  const off = c - (score / 100) * c;
  return (
    <div className="relative w-14 h-14 shrink-0">
      <svg width="56" height="56" viewBox="0 0 56 56" className="-rotate-90">
        <circle cx="28" cy="28" r={r} stroke="#F3EFEA" strokeWidth="4" fill="none" />
        <circle cx="28" cy="28" r={r} stroke="#85A389" strokeWidth="4" fill="none"
                strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
                style={{ transition: "stroke-dashoffset 0.6s ease" }} />
      </svg>
      <div className="absolute inset-0 grid place-items-center">
        <div className="text-xs font-medium">{score}<span className="text-[10px] text-muted-foreground">%</span></div>
      </div>
    </div>
  );
}
